import { useState, useEffect } from 'react';
import { LuCircleAlert, LuTruck } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import { fetchMyPartner, fetchTrucks, assignTruckToLoad, updateTruck, sendNotification } from '../../lib/directus';
import styles from './WonBidDrawer.module.css';

function ActionBanner({ bid }) {
  return (
    <div className={styles.actionBanner}>
      <LuCircleAlert size={18} color="#92400e" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <p className={styles.actionBannerTitle}>Action required</p>
        <p className={styles.actionBannerText}>
          Assign a truck and driver by {bid ? bid.assignBy : '-'}
        </p>
      </div>
    </div>
  );
}

function RouteCard({ bid }) {
  return (
    <div className={styles.routeCard}>
      <div className={styles.routeRow}>
        <div className={styles.pickupBlock}>
          <span className={styles.routeLabel}>PICKUP</span>
          <span className={styles.routeName}>{bid.pickup}</span>
        </div>
        <span className={styles.routeArrow}>→</span>
        <div className={styles.deliveryBlock}>
          <span className={styles.routeLabel}>DELIVERY</span>
          <span className={styles.routeName}>{bid.delivery}</span>
        </div>
      </div>
      <div className={styles.cardDivider} />
      <div className={styles.distanceRow}>
        <span className={styles.distanceLabel}>Distance &amp; Est time</span>
        <span className={styles.distanceValue}>{bid.distance} • {bid.estimatedTime}</span>
      </div>
    </div>
  );
}

export default function WonBidDrawer({ bid, onClose, onAssigned }) {
  const [view, setView]                   = useState('assign');
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [trucks, setTrucks]               = useState([]);
  const [loadingTrucks, setLoadingTrucks] = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState('');

  useEffect(() => {
    if (!bid) return;
    setLoadingTrucks(true);
    fetchMyPartner()
      .then(partner => fetchTrucks(partner?.id || null))
      .then(data => setTrucks(data.map(t => ({
        id:           t.id,
        name:         t.truck_id || `Truck #${String(t.id).slice(0, 4).toUpperCase()}`,
        make:         t.make || '-',
        capacity:     t.capacity != null ? `${t.capacity} MT` : '-',
        plate:        t.license_plate || '-',
        status:       t.status || 'Available',
        driverId:     t.driver?.id || null,
        driverUserId: t.driver?.user || null,
      }))))
      .catch(console.error)
      .finally(() => setLoadingTrucks(false));
  }, [bid]);

  if (!bid) return null;

  function handleClose() {
    setView('assign');
    setSelectedTruck(null);
    setError('');
    onClose();
  }

  async function handleConfirm() {
    if (!selectedTruck) return;
    setSubmitting(true);
    setError('');
    try {
      if (bid._loadId) {
        await assignTruckToLoad(bid._loadId, {
          assigned_truck:  selectedTruck.id,
          assigned_driver: selectedTruck.driverId || null,
          orderId:         bid.orderId,
          fulfillment_status: 'transport_sourced',
        });
        await updateTruck(selectedTruck.id, { status: 'In transit' });
        if (selectedTruck.driverUserId) {
          sendNotification(
            selectedTruck.driverUserId,
            'New load assigned to you',
            `You have been assigned a load from ${bid.pickup} to ${bid.delivery}. Please check your trips for details.`
          ).catch(console.error);
        }
      }
      setView('assign');
      setSelectedTruck(null);
      onAssigned && onAssigned(selectedTruck);
    } catch (err) {
      setError(err.message || 'Failed to assign truck');
    } finally {
      setSubmitting(false);
    }
  }

  const assignFooter = (
    <button
      className={`${styles.submitBtn} ${!selectedTruck || submitting ? styles.submitBtnDisabled : ''}`}
      disabled={!selectedTruck || submitting}
      onClick={handleConfirm}
    >
      {submitting ? 'Assigning...' : 'Confirm assignment'}
    </button>
  );

  return (
    <Drawer isOpen={!!bid} title={bid.loadId} onClose={handleClose} footer={assignFooter}>
      <ActionBanner bid={bid} />
      <RouteCard bid={bid} />

      <div className={styles.truckList}>
        <p className={styles.cardTitle} style={{ padding: '16px 20px 0', margin: 0 }}>Select truck</p>

        {loadingTrucks && (
          <p style={{ padding: '16px 20px', fontSize: 14, color: 'var(--secondary-text)' }}>Loading trucks...</p>
        )}

        {!loadingTrucks && trucks.length === 0 && (
          <p style={{ padding: '16px 20px', fontSize: 14, color: 'var(--secondary-text)' }}>No trucks found. Add trucks in your profile.</p>
        )}

        {trucks.map((truck, idx) => {
          const unavailable = truck.status.toLowerCase() === 'in transit';
          return (
          <div key={truck.id}>
            <div
              className={`${styles.truckRow} ${selectedTruck?.id === truck.id ? styles.truckRowSelected : ''} ${unavailable ? styles.truckRowDisabled : ''}`}
              onClick={() => { if (!unavailable) setSelectedTruck(truck); }}
            >
              <div className={styles.truckIcon}>
                <LuTruck size={16} color="#9ca3af" />
              </div>
              <div style={{ flex: 1 }}>
                <p className={styles.truckName}>{truck.name}</p>
                <p className={styles.truckMeta}>{truck.make} • {truck.capacity} • {truck.plate}</p>
              </div>
              {truck.status === 'Available' ? (
                <span className={styles.availableBadge}>{truck.status}</span>
              ) : (
                <span className={styles.inTransitBadge}>{truck.status}</span>
              )}
            </div>
            {idx < trucks.length - 1 && <div className={styles.truckDivider} />}
          </div>
          );
        })}

        {error && (
          <p style={{ padding: '12px 20px', fontSize: 13, color: 'var(--red-color)' }}>{error}</p>
        )}
      </div>
    </Drawer>
  );
}
